// The feed's unit: a SITUATION, not a report.
//
// An incident is a server-side object (migration 033) that a model routes
// reports into, so unlike frontend/src/situations.ts it is PERSISTENT: it keeps
// a title someone can fix, it accumulates, and it resurfaces when it grows.
//
// THE FALLBACK IS NOT OPTIONAL. Routing is the slow path — a report is on the
// map long before it is placed, and if the router is down it is never placed at
// all. So anything unrouted still goes through situations.ts exactly as it does
// today, and the card says so. That is what makes a router outage visible
// instead of a feed that quietly loses half its contents.

import type { EntityDto, IncidentDto } from './api';
import { del, get, post, put } from './api';
import { LIVE_KINDS } from './livekinds';
import { groupSituations, headlineOf, type Situation } from './situations';
import { lastReportOf, whenOf } from './when';

// --- state -------------------------------------------------------------------
// Keyed by id and merged from the /api/updates cursor, exactly as entities are.
// An incident's own fields do not ride its members' revs, which is why the
// payload carries them separately.

let byId = new Map<string, IncidentDto>();

export function knownIncidents(): Map<string, IncidentDto> { return byId; }

export async function loadIncidents(): Promise<void> {
  try {
    const dto = await get<{ incidents: IncidentDto[] }>('/api/incidents?status=open');
    byId = new Map(dto.incidents.map((i) => [i.id, i]));
  } catch {
    // A failed load must not blank the feed: the fallback grouping still works.
    byId = byId.size ? byId : new Map();
  }
}

export function mergeIncidents(incoming: IncidentDto[] | undefined): boolean {
  if (!incoming?.length) return false;
  for (const inc of incoming) {
    // A merged or dismissed incident is retired, not deleted — drop it from the
    // live view but never from the server, where its audit trail lives.
    if (inc.status === 'merged' || inc.status === 'dismissed') byId.delete(inc.id);
    else byId.set(inc.id, inc);
  }
  return true;
}

// --- grouping ----------------------------------------------------------------

export interface FeedGroup {
  key: string;
  title: string;
  /** Present when the server routed this group; absent for the local fallback. */
  incident?: IncidentDto;
  events: EntityDto[];          // newest activity first
  firstAt: number;
  lastAt: number;
  kinds: string[];
  channels: string[];
  reportCount: number;
  /** True when this group came from situations.ts because nothing had routed
   *  these reports yet. Rendered as a chip, so an outage is legible. */
  local: boolean;
}

/** What an OPEN group card is currently SHOWING, as one comparable string.
 *
 *  A group card is built on click and then sits there. Everything on it is
 *  live — the report count, the channel count, the ⤷ latest line, the headline
 *  itself (it upgrades in place as better reports arrive) and the extractor's
 *  confidence — so a merge landing while the card is open left it showing
 *  numbers the feed behind it had already replaced.
 *
 *  `rev` is the global change cursor and a merge writes a new VERSION, so every
 *  attrs change is covered here without enumerating the fields the card happens
 *  to read this month. That matters: the next field added to the card is then
 *  live for free, where a hand-listed set would silently go stale.
 *
 *  Comparing rather than repainting unconditionally is the point. The feed
 *  re-renders every 30 s, and rebuilding an open card on a tick that changed
 *  nothing would move it under the reader's hands for no reason — the same
 *  argument that shows a build chip instead of reloading the page.
 */
export function groupSignature(g: FeedGroup): string {
  return [g.key, g.title, g.lastAt, g.reportCount,
          g.incident?.memberCount, g.incident?.channelCount, g.incident?.title,
          ...g.events.map((e) => `${e.id}:${e.rev}`)].join('|');
}

/** The group an OPEN card is looking at now, after the feed re-grouped.
 *
 *  Matching on `key` alone is not enough, and the case that breaks it is
 *  routine rather than exotic: an unrouted card is keyed by its oldest event
 *  id and becomes `inc:<uuid>` the moment the router places it, ~20 s later.
 *  The card would find nothing, sit there stale, and keep wearing the "grouped
 *  locally" chip that exists precisely to say the router has not run yet.
 *
 *  So: key first, then the group holding the most of the card's own reports.
 *  Membership is what identity means here — a merge adds reports, a split
 *  takes some away, and routing renames the container without touching either.
 *  No overlap at all means these reports have left the feed (narrowed by kind,
 *  muted, aged out), and the caller leaves the card alone: it presents an
 *  OBJECT, and a filter does not close it.
 */
export function regroup(groups: FeedGroup[], key: string,
                        eventIds: Set<string>): FeedGroup | null {
  const exact = groups.find((g) => g.key === key);
  if (exact) return exact;
  let best: FeedGroup | null = null;
  let bestN = 0;
  for (const g of groups) {
    let n = 0;
    for (const e of g.events) if (eventIds.has(e.id)) n += 1;
    if (n > bestN) { bestN = n; best = g; }
  }
  return best;
}

/** "Where — what", mirroring the server's _title_for (incidents/router.py):
 *  the feed is scanned by place first, so routed and unrouted cards must open
 *  with the same word. The kind is the best-ESTABLISHED member's, not the
 *  newest — same pick as the card's headline, so the title never says
 *  something the body walks back. An unclear kind adds nothing and the place
 *  stands alone; a group with no usable place leads with the kind rather than
 *  rendering "(unknown) — Impact". */
function localTitle(sit: Situation): string {
  const kind = String(headlineOf(sit.events).attrs.kind ?? 'unclear');
  const label = kind !== 'unclear' ? LIVE_KINDS[kind]?.label : undefined;
  if (!sit.place || sit.place === '(unknown)') return label ?? sit.place;
  return label ? `${sit.place} — ${label}` : sit.place;
}

function fromSituation(sit: Situation): FeedGroup {
  return {
    key: sit.key, title: localTitle(sit), events: sit.events,
    firstAt: sit.firstAt, lastAt: sit.lastAt, kinds: sit.kinds,
    channels: sit.channels, reportCount: sit.reportCount, local: true,
  };
}

function build(inc: IncidentDto, events: EntityDto[]): FeedGroup {
  const ordered = [...events].sort((a, b) => lastReportOf(b) - lastReportOf(a));
  const kinds: string[] = [];
  const channels = new Set<string>();
  let reportCount = 0;
  // Oldest-first for the kind ladder, so the arc reads in the order it happened.
  for (const e of [...ordered].reverse()) {
    const kind = String(e.attrs.kind ?? 'unclear');
    if (!kinds.includes(kind)) kinds.push(kind);
    const sources = e.attrs.sources;
    if (Array.isArray(sources) && sources.length) {
      reportCount += sources.length;
      for (const s of sources) {
        const c = String((s as { channel?: unknown }).channel ?? '');
        if (c) channels.add(c.replace(/^@/, ''));
      }
    } else {
      reportCount += Number(e.attrs.sourceCount ?? 0) || 1;
      const c = String(e.attrs.channel ?? '');
      if (c) channels.add(c.replace(/^@/, ''));
    }
  }
  // The incident's OWN clock when it has one — it is the projection over every
  // member, including ones the client may not be holding.
  const first = inc.happenedAt ? Date.parse(inc.happenedAt)
    : Math.min(...ordered.map((e) => whenOf(e).at));
  const last = inc.lastReportAt ? Date.parse(inc.lastReportAt)
    : Math.max(...ordered.map(lastReportOf));
  return {
    key: `inc:${inc.id}`, title: inc.title, incident: inc, events: ordered,
    firstAt: first, lastAt: last, kinds, channels: [...channels],
    reportCount, local: false,
  };
}

// THE LOCAL GROUPING IS MEMOISED ON ITS INPUT. groupFeed runs three times per
// poll over the same unrouted set — the feed's cards, the unread badge, and the
// map bake — and every 30 s tick and read-state change repeats it; the input
// only actually changes when a poll delivers an entity. The key is every
// member's id and rev, so a merge (a new version of the same id) or a different
// selection of events is a different key, while the same set in the same order
// is a hit. Situations are immutable once built (nothing downstream sorts or
// splices `events`), so sharing them across callers is safe. A handful of
// entries, because the callers ask with a few different subsets of one list.
const LOOSE_MEMO_MAX = 6;
const looseMemo: { key: string; value: Situation[] }[] = [];

function groupLoose(loose: EntityDto[]): Situation[] {
  if (!loose.length) return [];
  const parts = new Array<string>(loose.length);
  for (let i = 0; i < loose.length; i++) parts[i] = `${loose[i]!.id}:${loose[i]!.rev}`;
  const key = parts.join('|');
  for (let i = 0; i < looseMemo.length; i++) {
    const m = looseMemo[i]!;
    if (m.key === key) {
      if (i) { looseMemo.splice(i, 1); looseMemo.unshift(m); }
      return m.value;
    }
  }
  const value = groupSituations(loose);
  looseMemo.unshift({ key, value });
  if (looseMemo.length > LOOSE_MEMO_MAX) looseMemo.length = LOOSE_MEMO_MAX;
  return value;
}

/** Partition the feed: server-routed incidents first, then the local fallback
 *  for whatever is still unrouted. Both shapes render identically apart from the
 *  chip, so the reader is never left wondering why two cards look different. */
export function groupFeed(events: EntityDto[]): FeedGroup[] {
  const routed = new Map<string, EntityDto[]>();
  const loose: EntityDto[] = [];
  for (const e of events) {
    const id = e.incidentId;
    // An incident we have never heard of (a poll we missed, a story we cannot
    // see) falls back rather than producing a card with no title.
    if (id && byId.has(id)) {
      const list = routed.get(id);
      if (list) list.push(e); else routed.set(id, [e]);
    } else {
      loose.push(e);
    }
  }
  const groups: FeedGroup[] = [];
  for (const [id, members] of routed) groups.push(build(byId.get(id)!, members));
  for (const sit of groupLoose(loose)) groups.push(fromSituation(sit));
  // Newest KNOWN activity first — a feed is a newsroom. An incident that grew
  // resurfaces here, while its age stays the occurrence clock.
  return groups.sort((a, b) => b.lastAt - a.lastAt);
}

/** Group the feed when a TIME WINDOW is in play.
 *
 *  The window's job is to decide which CARDS surface. It is not a claim about
 *  what a surfaced card is made of — and grouping only the in-window events
 *  made it one, silently. A Strait of Hormuz incident the server had filed 7
 *  reports into rendered a card whose badge read 7 and whose expansion held 3:
 *  the header count is the server's projection over EVERY member, while the
 *  list was whatever survived the 12 h window. So the one control the reader
 *  has for auditing a group — expand it, see the reports — was the control
 *  that lied, and the four missing reports were the OLD ones, i.e. exactly the
 *  ones that say how the situation began.
 *
 *  So: a card appears because something inside the window put it there, and
 *  once it appears it answers for the whole situation. `surfacing` is what the
 *  window admits; `context` is what it holds back. Groups made only of context
 *  are dropped — those are situations that finished before the window, and the
 *  "N older than the window" pager is what reaches them.
 *
 *  `attached` is what context contributed to a surfaced card, and the pager
 *  must stop counting it: a report reachable by expanding a card already on
 *  screen is not being held back from anyone. Counting it there would page in
 *  rows that are already rendered — the inverse of the silent-hiding bug, and
 *  just as confusing.
 *
 *  This stays cheap for the same reason the unread badge can afford
 *  groupFeed(liveEvents) on every render: routed members attach by incident id
 *  in one pass, and the O(n²) situation fallback already runs over the whole
 *  unfiltered set once per render. */
export function groupFeedWindowed(surfacing: EntityDto[], context: EntityDto[]):
    { groups: FeedGroup[]; attached: string[] } {
  if (!context.length) return { groups: groupFeed(surfacing), attached: [] };
  const admitted = new Set(surfacing.map((e) => e.id));
  const groups = groupFeed([...surfacing, ...context])
    .filter((g) => g.events.some((e) => admitted.has(e.id)));
  const attached = groups.flatMap((g) => g.events
    .filter((e) => !admitted.has(e.id)).map((e) => e.id));
  return { groups, attached };
}

// --- correction ---------------------------------------------------------------
// First-class, because a stored wrong merge destroys a distinction where the old
// client-side grouping merely hid one — expanding the card used to be the undo.

export async function renameIncident(id: string, title: string): Promise<IncidentDto> {
  const saved = await put<IncidentDto>(`/api/incidents/${id}/title`, { title });
  byId.set(id, saved);
  return saved;
}

export async function splitOut(id: string, ids: string[],
                               title: string): Promise<IncidentDto> {
  const res = await post<{ incident: IncidentDto }>(
    `/api/incidents/${id}/split`, { ids, title });
  byId.set(res.incident.id, res.incident);
  return res.incident;
}

export async function detach(id: string, ids: string[]): Promise<void> {
  await del(`/api/incidents/${id}/members`, { ids });
}

// THE UNDO FOR UNDER-MERGING, and it has to exist here for the routing default
// to be honest. The matcher (like the judge before it) is priced on "opening a
// second incident for one event is untidy and can be merged in one click" —
// so the click has to exist. Split (the undo for over-merging) was first-class
// from the start while this was not reachable at all, so the cheap direction
// was the one with no affordance and the expensive one had a button.
// Retire-and-point: the loser keeps its history and its audit trail.
export async function mergeInto(id: string, into: string): Promise<IncidentDto> {
  const res = await post<{ incident: IncidentDto }>(
    `/api/incidents/${id}/merge`, { into });
  // The loser is retired, not deleted; drop it so its reports regroup under the
  // winner on the next render instead of vanishing.
  byId.delete(id);
  byId.set(res.incident.id, res.incident);
  return res.incident;
}

export async function incidentDetail(id: string): Promise<IncidentDto> {
  const inc = await get<IncidentDto>(`/api/incidents/${id}`);
  byId.set(id, inc);
  return inc;
}

/** Replace the curator-attached link list. Server enforces http(s) and caps. */
export async function setIncidentLinks(
    id: string, links: { url: string; label?: string }[]): Promise<IncidentDto> {
  const saved = await put<IncidentDto>(`/api/incidents/${id}/links`, { links });
  byId.set(id, saved);
  return saved;
}

// --- merge suggestions --------------------------------------------------------
// The advisor's queue: routing is deterministic now, and the model's remaining
// job is naming pairs of open incidents that describe one event — "Jazan
// Refinery" / "Aramco Jazan Refinery", the class no rule can see. A suggestion
// is ADVICE: nothing merges until someone accepts it here, which is what the
// old judge's "joining wrongly destroys a distinction" asymmetry demands once
// the joiner is a model.

export interface MergeSuggestionDto {
  id: string;
  sourceId: string;
  targetId: string;
  sourceTitle: string;
  targetTitle: string;
  why: string;
  confidence: string | null;
  createdAt: string;
}

let openSuggestions: MergeSuggestionDto[] = [];

/** Open suggestions naming this incident, either side. */
export function suggestionsFor(incidentId: string): MergeSuggestionDto[] {
  return openSuggestions.filter(
    (s) => s.sourceId === incidentId || s.targetId === incidentId);
}

export async function loadSuggestions(): Promise<void> {
  try {
    const dto = await get<{ suggestions: MergeSuggestionDto[] }>(
      '/api/incidents/suggestions');
    openSuggestions = dto.suggestions;
  } catch {
    // A failed load keeps the last known queue; the chip is advisory only.
  }
}

export async function applySuggestion(id: string): Promise<void> {
  await post(`/api/incidents/suggestions/${id}/apply`);
  openSuggestions = openSuggestions.filter((s) => s.id !== id);
  // The merge retired one incident and grew another; refresh both maps.
  await Promise.all([loadIncidents(), loadSuggestions()]);
}

export async function dismissSuggestion(id: string): Promise<void> {
  await post(`/api/incidents/suggestions/${id}/dismiss`);
  openSuggestions = openSuggestions.filter((s) => s.id !== id);
}
